<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'rentals',
    path: '/rentals',
    component: 'RentalBrowser',
    title: () => gettext('Rentals'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RentalBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    if (dsParams.value.actives_only) {
      return { filter: 'inactive eq false' };
    }
  };

  const selectedItems = ref([]);
  const dsParams = ref({
    actives_only: true,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'inactive',
      name: $fn.value.pgettext('Rentals', 'Inactive'),
      textAlign: 'center',
      formatter: {
        name: 'checkbox',
      },
    },
    {
      field: 'name',
      name: $fn.value.pgettext('Rentals', 'Name'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'rental',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'description',
      name: $fn.value.pgettext('Rentals', 'Description'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'rental',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'categories',
      name: $fn.value.pgettext('Rentals', 'Category'),
    },
    {
      field: 'valid_from',
      name: $fn.value.pgettext('Rentals', 'Valid From'),
      type: 'date',
    },
    {
      field: 'valid_to',
      name: $fn.value.pgettext('Rentals', 'Valid To'),
      type: 'date',
    },
    {
      field: 'site_name',
      name: $fn.value.pgettext('Rentals', 'Site'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'property',
        navigateOnIconClick: true,
        params: {
          id: 'site_id',
        },
      },
    },
    {
      field: 'property_units',
      name: $fn.value.pgettext('Rentals', 'Property units'),
      toolTip: $fn.value.pgettext('Rentals', 'Property units'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'code',
              source: 'value',
            },
          },
        ],
      },
    },
    {
      field: 'location_code',
      name: $fn.value.pgettext('Rentals', 'Location Code'),
    },
    {
      field: 'lot_number',
      name: $fn.value.pgettext('Rentals', 'Lot Number'),
    },
    {
      field: 'house_number',
      name: $fn.value.pgettext('Rentals', 'House Number'),
    },
    {
      field: 'area',
      name: $fn.value.pgettext('Rentals', 'Area'),
      type: 'text',
    },
    {
      field: 'company_name',
      name: $fn.value.pgettext('Rentals', 'Company'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'company_id',
        },
      },
    },
    {
      field: 'current_tenant_name',
      name: $fn.value.pgettext('Rentals', 'Tenant'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'current_tenant_id',
        },
      },
    },
    {
      field: 'current_contract_number',
      name: $fn.value.pgettext('Rentals', 'Contract'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'contract',
        navigateOnIconClick: true,
        params: {
          id: 'current_contract_id',
        },
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_rental', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'RentalBrowser.dsParams' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      dsParams,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Rentals'),
      icon: $config.value.rental.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_jsx062ffb_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_hx94ne6wz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_15dwwz4j5_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CompanyModal',
      props: {
        id: $event.data.data.company_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_fjtnjn5r7_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'company_name';
  };
  const act_2095089031469157_showModal_wnyifepcs_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateRental',
    };
  };

  const act_7315092382398562_closeModal_jsx062ffb =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_jsx062ffb_getActionArgs,
    });
  const inst_evh_7315092382398562_close_jlb7z9wja =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_jsx062ffb,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_jlb7z9wja],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_2h5jio1im =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_dwbrmd0mo =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_2h5jio1im,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_dwbrmd0mo],
    event: 'reload',
  });
  const act_2248226175642056_setData_hx94ne6wz = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_hx94ne6wz_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_8jbuakww6 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_hx94ne6wz,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_8jbuakww6],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_15dwwz4j5 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_15dwwz4j5_getActionArgs,
    });
  const inst_evh_2248226175642056_command_fjtnjn5r7 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_15dwwz4j5,
      event: 'command',
      when: inst_evh_2248226175642056_command_fjtnjn5r7_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_fjtnjn5r7],
    event: 'command',
  });
  const act_2095089031469157_showModal_wnyifepcs =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_wnyifepcs_getActionArgs,
    });
  const inst_evh_2095089031469157_click_lrljel11e =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_wnyifepcs,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_lrljel11e],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_jlb7z9wja,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_dwbrmd0mo,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_8jbuakww6,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_fjtnjn5r7,
    inst_evh_2248226175642056_command,
    inst_evh_2095089031469157_click_lrljel11e,
    inst_evh_2095089031469157_click,
  };

  const evh_7315092382398562_close_jlb7z9wja = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_jlb7z9wja.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_dwbrmd0mo = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_dwbrmd0mo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_8jbuakww6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_8jbuakww6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_fjtnjn5r7 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_fjtnjn5r7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_lrljel11e = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_lrljel11e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RentalBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="rentals"
      :headerTitle="gettext('Rentals')"
      :items="ds"
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'rentals')"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <ItpBox alignItems="center" :gap="2" class="flex-grow-1">
          <div>
            <ItpFormCheckbox
              name="actives_only"
              :label="pgettext('RentalBrowser', 'Display active rentals only')"
              inline
              v-model="dsParams.actives_only"
              :value="dsParams.actives_only"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (dsParams.actives_only =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpButton
            :icon="$config.rental.addRentalIcon"
            :text="pgettext('RentalBrowser', 'Add rental...')"
            size="sm"
            variant="light"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/rentals/rentals.html" class="ms-auto"> </PageHelp>
        </ItpBox>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
